import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Builds the downloadable per-successor PDF report (Sprint-2 §E) from the payload
 * returned by GET /v1/succession-candidate/report/:id.
 *
 * Design goals: neat, on-brand, and breakproof — every field is guarded with a
 * fallback, long text wraps, and the cursor paginates so nothing is ever clipped
 * or written off the page regardless of how much data a successor has.
 */

// Spotlight palette
const NAVY: [number, number, number] = [31, 58, 95]; // headers / bands
const ORANGE: [number, number, number] = [232, 131, 42]; // accent
const LIGHT: [number, number, number] = [245, 247, 250]; // zebra / fills
const INK: [number, number, number] = [33, 37, 41];
const MUTED: [number, number, number] = [120, 128, 138];

const MARGIN = 40;

// --- 9-box helpers (mirror CandidateDevelopment.tsx) ---
const LEVEL_SCORE: any = { High: 3, Medium: 2, Low: 1 };
const BOX_CATEGORY: any = {
  9: "Future Leader", 8: "High Potential", 7: "Emerging Potential",
  6: "Strong Performer", 5: "Core Talent", 4: "Low Performer",
  3: "Specialist", 2: "Inconsistent", 1: "At Risk",
};
const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = LEVEL_SCORE[v] ?? Number(v);
  return Number.isFinite(n) && n >= 1 && n <= 3 ? n : null;
};
const boxFromNums = (perfN: number | null, potN: number | null) =>
  perfN && potN ? potN * 3 - (3 - perfN) : null;

const dash = (v: any) => {
  if (v === null || v === undefined) return "-";
  const s = String(v).trim();
  return s === "" ? "-" : s;
};
const fmtDate = (v: any) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
};

type R = any;

export const downloadSuccessorReportPdf = (report: R) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - MARGIN * 2;

  const emp = report?.employee || {};
  const seat = report?.seat || {};
  const kra = report?.kra || {};
  const asmt = report?.assessment || {};

  // running vertical cursor
  let y = 0;

  // ensure there is room for `need` more points; new page if not
  const ensure = (need: number) => {
    if (y + need > pageH - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  // ---- top brand band ----
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 70, "F");
  doc.setFillColor(...ORANGE);
  doc.rect(0, 70, pageW, 4, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Succession Report", MARGIN, 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(222, 230, 240);
  doc.text(dash(emp.name), MARGIN, 52);
  doc.setFontSize(8);
  doc.text(`Generated ${fmtDate(report?.generatedAt || new Date())}`, pageW - MARGIN, 52, { align: "right" });

  y = 94;

  // ---- section title ----
  const sectionTitle = (label: string) => {
    y += 10; // breathing room above each section
    ensure(40);
    doc.setFillColor(...LIGHT);
    doc.rect(MARGIN, y, contentW, 22, "F");
    doc.setFillColor(...ORANGE);
    doc.rect(MARGIN, y, 3, 22, "F");
    doc.setTextColor(...NAVY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(label, MARGIN + 10, y + 15);
    y += 34;
  };

  // ---- two-column key/value grid ----
  const kvGrid = (pairs: Array<[string, string]>) => {
    const colW = contentW / 2;
    const lineH = 12;
    for (let i = 0; i < pairs.length; i += 2) {
      const row = [pairs[i], pairs[i + 1]].filter(Boolean) as Array<[string, string]>;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      const cells = row.map(([label, value]) => ({
        label, lines: doc.splitTextToSize(dash(value), colW - 14) as string[],
      }));
      const lineCount = Math.max(1, ...cells.map((cell) => cell.lines.length));
      const rowH = 28 + (lineCount - 1) * lineH;
      // Keep normal rows together; paginate exceptionally long values without clipping.
      ensure(Math.min(rowH, pageH - 2 * MARGIN));
      let offset = 0;
      while (offset < lineCount) {
        ensure(28);
        const capacity = Math.max(1, Math.floor((pageH - MARGIN - y - 28) / lineH) + 1);
        const count = Math.min(capacity, lineCount - offset);
        cells.forEach((cell, col) => {
          const x = MARGIN + col * colW;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(...MUTED);
          doc.text(cell.label.toUpperCase(), x, y);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(...INK);
          const lines = cell.lines.slice(offset, offset + count);
          if (lines.length) doc.text(lines, x, y + 13, { lineHeightFactor: 1.2 });
        });
        y += 28 + (count - 1) * lineH;
        offset += count;
      }
    }
    y += 4;
  };

  // ---- free-text paragraph block ----
  const paragraph = (text: string, meta?: string) => {
    const body = dash(text);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    const lines = doc.splitTextToSize(body, contentW - 4);
    const lineH = 13;
    // write line by line so a long note paginates cleanly
    for (const ln of lines) {
      ensure(lineH + 2);
      doc.text(ln, MARGIN + 2, y);
      y += lineH;
    }
    if (meta) {
      ensure(12);
      doc.setFontSize(7.5);
      doc.setTextColor(...MUTED);
      doc.text(meta, MARGIN + 2, y);
      y += 12;
    }
    y += 10;
  };

  // run an autoTable and advance the cursor to below it
  const table = (head: string[], rows: any[][]) => {
    ensure(40);
    autoTable(doc, {
      startY: y,
      head: [head],
      body: rows.length ? rows : [head.map(() => "-")],
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 8.5, cellPadding: 5, textColor: INK, overflow: "linebreak", valign: "middle" },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5 },
      alternateRowStyles: { fillColor: LIGHT },
      theme: "grid",
    });
    y = (doc as any).lastAutoTable.finalY + 16;
  };

  // ===== 1. Candidate & seat =====
  sectionTitle("Candidate & Target Role");
  kvGrid([
    ["Employee", emp.name],
    ["Employee ID", emp.employeeId],
    ["Current Designation", emp.designation],
    ["Department", emp.department],
    ["Current Client", emp.clientName],
    ["Email", emp.email],
    ["Succeeding (Role)", seat.designationTitle],
    ["Role Client", seat.clientName],
    ["Criticality", seat.criticality],
    ["Seat Risk Level", seat.riskLevel],
  ]);

  // ===== 2. KRA score =====
  sectionTitle("KRA Score");
  kvGrid([
    ["Pre-Development KRA", kra.preScore ?? "Not scored yet"],
    ["Final KRA (latest)", kra.finalScore ?? "Not scored yet"],
    ["Scored Against", kra.scoredDesignationTitle],
    ["Cross-Designation Score", kra.isCrossDesignation ? "Yes" : "No"],
  ]);

  // ===== 3. Assessment (9-box) =====
  const perfNum = toNum(asmt.postPerformance) ?? toNum(asmt.performance);
  const potNum = toNum(asmt.postPotential) ?? toNum(asmt.potential);
  const box = boxFromNums(perfNum, potNum);
  sectionTitle("Succession Assessment");
  kvGrid([
    ["Readiness", asmt.readiness],
    ["Risk of Loss", asmt.riskOfLoss],
    ["Performance (pre)", asmt.performance],
    ["Potential (pre)", asmt.potential],
    ["Performance (post)", asmt.postPerformance],
    ["Potential (post)", asmt.postPotential],
    ["9-Box Position", box ? `${BOX_CATEGORY[box]} (Box ${box})` : "-"],
    ["Last Assessed", fmtDate(asmt.lastAssessedAt)],
  ]);

  // ===== 4. Qualitative feedback (the 5 boxes) =====
  sectionTitle("Qualitative Feedback");
  const qual: R[] = Array.isArray(report?.qualitative) ? report.qualitative : [];
  if (!qual.length) {
    paragraph("-");
  } else {
    qual.forEach((q, i) => {
      if (i > 0) y += 6; // gap between boxes
      ensure(30);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...NAVY);
      doc.text(dash(q.label), MARGIN + 2, y);
      y += 16;
      // author · date only — no word count in the report
      const metaParts = [q.by, q.at ? fmtDate(q.at) : ""].filter((s) => s && String(s).trim());
      const meta = q.text && metaParts.length ? metaParts.join(" · ") : undefined;
      paragraph(q.text, meta);
    });
  }

  // ===== 5. Progress feedback (§C) =====
  sectionTitle("Development Progress Feedback");
  const pf = report?.progressFeedback || {};
  const pfMeta = pf.text ? `${pf.by ? pf.by : ""}${pf.at ? `${pf.by ? " · " : ""}${fmtDate(pf.at)}` : ""}` : undefined;
  paragraph(pf.text, pfMeta || undefined);

  // ===== 6. Development plan =====
  sectionTitle("Development Plan");
  const plans: R[] = Array.isArray(report?.developmentPlans) ? report.developmentPlans : [];
  table(
    ["Gap", "Action", "Reporting Manager", "Start", "Target", "Status"],
    plans.map((p) => [
      dash(p.gap), dash(p.action), dash(p.owner), fmtDate(p.startDate), fmtDate(p.targetDate), dash(p.status),
    ])
  );

  // ===== 7. Assessment history =====
  sectionTitle("Assessment History");
  const hist: R[] = Array.isArray(report?.history) ? report.history : [];
  table(
    ["Type", "Readiness", "Perf", "Pot", "Risk", "Assessed", "By"],
    hist.map((h) => [
      dash(h.assessmentType), dash(h.readiness), dash(h.performance), dash(h.potential),
      dash(h.riskOfLoss), fmtDate(h.assessedAt), dash(h.assessedBy),
    ])
  );

  // ---- footer page numbers on every page ----
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text("Spotlight · Succession Planning", MARGIN, pageH - 20);
    doc.text(`Page ${i} of ${pages}`, pageW - MARGIN, pageH - 20, { align: "right" });
  }

  const safeName = dash(emp.name).replace(/[^\w.-]+/g, "_");
  doc.save(`Succession_Report_${safeName}.pdf`);
};
